import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ── Track registry ──────────────────────────────────────────────────────────────
// Add an entry here for each .mp3 you place in public/music/.
export const MUSIC_TRACKS = {
  home:   '/music/home.mp3',
  lobby:  '/music/lobby.mp3',
  reveal: '/music/reveal.mp3',
};

const MUTED_KEY   = 'tmg_music_muted';
const TARGET_VOL  = 0.35;   // master volume (0–1)
const FADE_IN_MS  = 900;
const FADE_OUT_MS = 600;

// ── Module-level audio singleton ────────────────────────────────────────────────
// Kept outside React so it survives re-renders and route transitions.
let _audio    = null;
let _fadeRaf  = null;
let _currentSrc = '';

function getAudio() {
  if (!_audio) {
    _audio = new Audio();
    _audio.loop   = true;
    _audio.volume = 0;
  }
  return _audio;
}

function cancelFade() {
  if (_fadeRaf) { cancelAnimationFrame(_fadeRaf); _fadeRaf = null; }
}

function fadeVolume(target, durationMs, onDone) {
  cancelFade();
  const audio = getAudio();
  const start     = audio.volume;
  const startTime = performance.now();

  function step(now) {
    const t    = Math.min((now - startTime) / durationMs, 1);
    audio.volume = Math.max(0, Math.min(1, start + (target - start) * t));
    if (t < 1) {
      _fadeRaf = requestAnimationFrame(step);
    } else {
      _fadeRaf = null;
      onDone?.();
    }
  }
  _fadeRaf = requestAnimationFrame(step);
}

// ── Context ─────────────────────────────────────────────────────────────────────
const MusicContext = createContext(null);

export function MusicProvider({ children }) {
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(MUTED_KEY) === 'true'; } catch { return false; }
  });
  const [currentTrack, setCurrentTrackState] = useState(null);

  // hasInteracted: browsers block autoplay until the user makes a gesture.
  // We listen for the first click/keydown then unlock playback.
  const [hasInteracted, setHasInteracted] = useState(false);
  const pendingPlayRef = useRef(false);   // set true if we tried to play before interaction

  useEffect(() => {
    if (hasInteracted) return;
    function onInteract() {
      setHasInteracted(true);
      // If a track was waiting, the state change will re-run the play effect below.
    }
    document.addEventListener('click',   onInteract, { once: true });
    document.addEventListener('keydown', onInteract, { once: true });
    return () => {
      document.removeEventListener('click',   onInteract);
      document.removeEventListener('keydown', onInteract);
    };
  }, [hasInteracted]);

  // ── Audio engine ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const audio = getAudio();
    const src   = currentTrack ? MUSIC_TRACKS[currentTrack] : null;

    if (!src || muted) {
      // Fade out then pause
      fadeVolume(0, FADE_OUT_MS, () => { audio.pause(); });
      return;
    }

    if (!hasInteracted) {
      // Can't autoplay yet — mark pending; effect will re-run after first gesture.
      pendingPlayRef.current = true;
      return;
    }

    pendingPlayRef.current = false;

    const fullSrc = new URL(src, window.location.href).href;
    if (_currentSrc !== fullSrc) {
      // Switch track: fade out existing audio, swap src, fade in.
      cancelFade();
      audio.pause();
      _currentSrc    = fullSrc;
      audio.src      = src;
      audio.volume   = 0;
      audio.load();
      audio.play().catch(() => {
        // Play blocked (e.g. file missing or strict autoplay policy) — fail silently.
      });
      fadeVolume(TARGET_VOL, FADE_IN_MS);
    } else if (audio.paused) {
      // Same track, just resume (e.g. after unmute).
      audio.play().catch(() => {});
      fadeVolume(TARGET_VOL, FADE_IN_MS);
    }
  }, [muted, hasInteracted, currentTrack]);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem(MUTED_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  const setTrack = useCallback((track) => {
    setCurrentTrackState(track ?? null);
  }, []);

  return (
    <MusicContext.Provider value={{ muted, toggleMute, setTrack, currentTrack }}>
      {children}
    </MusicContext.Provider>
  );
}

// ── Hooks ────────────────────────────────────────────────────────────────────────
export function useMusic() {
  return useContext(MusicContext);
}

/**
 * Call inside a page/component to declare which background track it wants.
 * The track starts on mount and stops when the component unmounts (unless
 * another component immediately claims a new track in the same render cycle).
 */
export function useBackgroundTrack(track) {
  const { setTrack } = useMusic();
  useEffect(() => {
    setTrack(track);
    return () => setTrack(null);
  }, [track, setTrack]);
}

// ── MusicControls ────────────────────────────────────────────────────────────────
// Inline controls rendered inside the settings menu (Layout.jsx).
export function MusicControls() {
  const { muted, toggleMute } = useMusic();
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={toggleMute}
      aria-pressed={!muted}
      className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors t-focus-ring ${
        muted ? 't-bg-input t-text-secondary hover:opacity-80' : 't-btn-primary'
      }`}
    >
      {muted ? t('music.unmute') : t('music.mute')}
    </button>
  );
}
